import { AlertTriangle, Clock, PowerOff, Search } from "lucide-react";

/**
 * Filter produk BERSAMA — Master Produk (`pages/InventoryProducts.jsx`) dan
 * Price List (`pages/PriceList.jsx`).
 *
 * Brief owner 22 Sep 2026: "Buatkan tab filter sama persis dengan yg ada pada
 * master produk". Dua salinan markup filter pasti berpisah jalan (pelajaran
 * komponen pemilih template yang pernah tersalin di 6 berkas), jadi kontrolnya
 * hidup DI SINI saja; tiap halaman hanya menambah kontrol khususnya lewat
 * `children` (Master Produk: urutan + jumlah; Price List: cabang, belum ada
 * harga).
 *
 * Pilihan dropdown datang dari REGISTRY lewat server
 * (`master_opsi.opsi_filter_produk`, dikirim `GET /inventory/master-opsi`
 * sebagai `filter_produk` dan `GET /inventory/price-list` sebagai `opsi`) —
 * BUKAN dari produk yang sedang tampil. Price List dulu menyusunnya dari hasil
 * yang sudah tersaring, sehingga memilih satu brand melenyapkan brand lain.
 *
 * Semua penyaringan dikerjakan SERVER (`ubah` hanya memperbarui state
 * halaman) — daftar dipotong per halaman, dan menyaring di layar membuat baris
 * di halaman berikutnya tidak pernah ketemu.
 *
 * `testid` dioper per halaman supaya data-testid lama tidak berubah:
 * Master Produk tetap `inv-prod-*`, Price List memakai `pl-*`.
 */

export const TESTID_MASTER_PRODUK = {
  cari: "inv-prod-search", kategori: "inv-prod-kategori", kategoriCustom: "inv-prod-subkategori",
  brand: "inv-prod-fbrand", gudang: "inv-prod-fgudang", stok: "inv-prod-fstok",
  supplier: "inv-prod-fsupplier", gagal: "inv-prod-filter-gagal", stokLama: "inv-prod-faging",
  nonaktif: "inv-prod-finactive",
};

export const TESTID_PRICE_LIST = {
  cari: "pl-cari", kategori: "pl-kategori", kategoriCustom: "pl-sub-kategori",
  brand: "pl-brand", gudang: "pl-gudang", stok: "pl-fstok",
  supplier: "pl-fsupplier", gagal: "pl-filter-gagal", stokLama: "pl-faging",
  nonaktif: "pl-finactive",
};

// Nilai filter kosong — SATU sumber untuk keadaan awal & "Bersihkan filter".
export const FILTER_PRODUK_KOSONG = {
  kategori: "", sub_kategori: "", brand: "", warehouse_id: "", supplier: "",
  // "" = semua | kosong (= 0) | ada (> 0) | minus (< 0) — minus sengaja kelompok
  // sendiri: menyembunyikannya di "kosong"/"ada" sama-sama menipu.
  stok: "",
  only_aging: false, include_inactive: false,
};

/**
 * Bentuk server (`{kategori: [kunci], kategori_label: {kunci: label},
 * sub_kategori: [], brand: []}`) -> bentuk komponen. `null` kalau server belum
 * menjawab, supaya pemanggil bisa memilih cadangannya sendiri.
 */
export function opsiDariServer(o) {
  if (!o || !Array.isArray(o.kategori)) return null;
  const label = o.kategori_label || {};
  return {
    kategori: o.kategori.map(v => ({ v, l: label[v] || v })),
    kategoriCustom: o.sub_kategori || [],
    brand: o.brand || [],
  };
}

const OPSI_KOSONG = { kategori: [], kategoriCustom: [], brand: [] };
const pilih = "h-9 px-2 border border-slate-200 rounded-lg text-sm";

export default function FilterProduk({
  nilai, ubah, cari, opsi, gudang = [], suppliers = [],
  gudangGagal = false, supplierGagal = false, agingHari = 60,
  testid = TESTID_MASTER_PRODUK, className = "", children,
}) {
  const o = opsi || OPSI_KOSONG;
  return (
    <div className={className}>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={cari.nilai} onChange={e => cari.onChange(e.target.value)}
               onKeyDown={e => { if (e.key === "Enter" && cari.onEnter) cari.onEnter(); }}
               placeholder={cari.placeholder || "Cari SKU / nama / barcode"} data-testid={testid.cari}
               aria-label="Cari SKU, nama, atau barcode produk"
               className="h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-60" />
      </div>
      {/* Poin 7 (brief 23 Agu): "Filter Kategori — urutan logis, bukan A-Z" —
          urutannya urutan Kategori Utama dari registry, tidak diurut ulang. */}
      <select value={nilai.kategori} onChange={e => ubah({ kategori: e.target.value })}
              data-testid={testid.kategori} aria-label="Filter kategori produk" className={pilih}>
        <option value="">Semua kategori</option>
        {o.kategori.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
      </select>
      {/* Poin 7: "urutan ketik admin" — TIDAK diurut ulang, dibiarkan
          persis urutan `kategori_custom[]` dari Pengaturan. */}
      {o.kategoriCustom.length > 0 && (
        <select value={nilai.sub_kategori} onChange={e => ubah({ sub_kategori: e.target.value })}
                data-testid={testid.kategoriCustom} aria-label="Filter kategori custom" className={pilih}>
          <option value="">Semua kat. custom</option>
          {o.kategoriCustom.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      )}
      {/* Poin 6: filter ini lintas kategori (bukan form produk), jadi memakai
          gabungan brand_gadget+brand_accessories. Poin 7: "urutan ketik admin". */}
      {o.brand.length > 0 && (
        <select value={nilai.brand} onChange={e => ubah({ brand: e.target.value })}
                data-testid={testid.brand} aria-label="Filter brand" className={pilih}>
          <option value="">Semua brand</option>
          {o.brand.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      )}
      {/* Poin 7: gudang/supplier di-sort SERVER dengan `.sort("nama",1)` yang
          case-SENSITIVE (byte-order — "Zebra" < "apple"). Diurut ULANG di
          klien dengan `localeCompare("id")` supaya huruf besar/kecil tidak
          memecah urutan A-Z. */}
      {gudang.length > 0 && (
        <select value={nilai.warehouse_id} onChange={e => ubah({ warehouse_id: e.target.value })}
                data-testid={testid.gudang} aria-label="Filter gudang" className={pilih}>
          <option value="">Semua gudang</option>
          {[...gudang].sort((a, b) => (a.nama || "").localeCompare(b.nama || "", "id"))
            .map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
        </select>
      )}
      {/* P4 (brief 4 Sep): saring berdasarkan qty, di SERVER supaya total &
          paginasi ikut. Saat gudang dipilih, qty yang dibandingkan adalah qty
          DI GUDANG ITU, jadi "stok kosong" berarti kosong di gudang itu. */}
      <select value={nilai.stok} onChange={e => ubah({ stok: e.target.value })}
              data-testid={testid.stok} aria-label="Filter stok" className={pilih}>
        <option value="">Semua stok</option>
        <option value="ada">Ada stok (&gt; 0)</option>
        <option value="kosong">Stok kosong (= 0)</option>
        <option value="minus">Stok minus (&lt; 0)</option>
      </select>
      {suppliers.length > 0 && (
        <select value={nilai.supplier} onChange={e => ubah({ supplier: e.target.value })}
                data-testid={testid.supplier} aria-label="Filter supplier" className={pilih}>
          <option value="">Semua supplier</option>
          {[...suppliers].sort((a, b) => (a.nama || "").localeCompare(b.nama || "", "id"))
            .map(s => <option key={s.id} value={s.nama}>{s.nama}</option>)}
        </select>
      )}
      {/* Diam-diam kosong bukan pilihan: dropdown yang hilang karena gagal
          dimuat tidak bisa dibedakan dari "memang belum ada datanya". */}
      {(gudangGagal || supplierGagal) && (
        <span data-testid={testid.gagal}
              title="Daftar ini dimuat terpisah dari daftar produk; produknya sendiri tetap tampil."
              className="h-9 px-2 inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5" />
          Filter {[gudangGagal && "gudang", supplierGagal && "supplier"].filter(Boolean).join(" & ")} gagal dimuat
        </span>
      )}
      <button type="button" onClick={() => ubah({ only_aging: !nilai.only_aging })} data-testid={testid.stokLama}
              title={`Barang yang nangkring >= ${agingHari} hari (atur ambangnya di Pengaturan)`}
              className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${
                nilai.only_aging ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
        <Clock className="w-3.5 h-3.5" /> Stok lama
      </button>
      <button type="button" onClick={() => ubah({ include_inactive: !nilai.include_inactive })} data-testid={testid.nonaktif}
              title="Tampilkan juga produk yang sudah dinonaktifkan"
              className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${
                nilai.include_inactive ? "border-slate-400 bg-slate-100 text-slate-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
        <PowerOff className="w-3.5 h-3.5" /> Termasuk nonaktif
      </button>
      {children}
    </div>
  );
}
